import { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getGroupInUseAffectedResources } from "@/shared/hooks/useAffectedResources";
import { getRequiresConfirmationReport } from "@/shared/hooks/useResourceUsageConfirmation";
import {
  extract422ErrorsDescription,
  extractApiErrorDetail,
  isAuthFailure,
  isClientError,
  isForbiddenError,
} from "@/shared/utils/errorHandling";
import { invalidateCentralResources, type CentralResource } from "./cacheInvalidations";

const shouldRetry = (failureCount: number, error: unknown, maxRetries: number): boolean => {
  // Un 4xx est une réponse délibérée : le rejouer redonne la même réponse.
  // Retenter une mutation refusée (422) la laissait de surcroît « pending »
  // le temps du retry - et indéfiniment hors ligne - donc ni toast d'erreur
  // ni fermeture de modale pendant ce temps (ANO-20).
  if (isAuthFailure(error) || isClientError(error)) return false;
  return failureCount < maxRetries;
};

// Meta configuration for mutations
type MutationMeta = {
  successMessage?: string
  errorMessage?: string
  invalidateQueries?: string[][]
  invalidateResources?: readonly CentralResource[]
  skipSuccessToast?: boolean
  showApiErrorDetail?: boolean
};

/**
 * Wire the global mutation handlers that read mutation.meta to:
 *   - run declarative invalidations (invalidateQueries + invalidateResources)
 *   - show success/error toasts
 *
 * Extracted so that test query clients can opt into the same behavior as
 * the production singleton.
 */
export const setupMutationMetaHandlers = (client: QueryClient): void => {
  const mutationCache = client.getMutationCache();

  mutationCache.config.onSuccess = (_data, _variables, _context, mutation) => {
    const meta = mutation.meta as MutationMeta;

    if (meta?.invalidateQueries) {
      meta.invalidateQueries.forEach((queryKey) => {
        client.invalidateQueries({ queryKey });
      });
    }

    if (meta?.invalidateResources?.length) {
      invalidateCentralResources(client, meta.invalidateResources);
    }

    if (meta?.successMessage && !meta.skipSuccessToast) {
      toast.success(meta.successMessage);
    }
  };

  mutationCache.config.onError = (error, _variables, _context, mutation) => {
    const meta = mutation.meta as MutationMeta;

    // ADM-856: a 409 requires_confirmation is a business flow the confirmation
    // dialog handles, not a failure — mutations that rely on this meta-driven
    // toast (e.g. groups) must not also surface a parasitic generic error toast.
    // Mutations that toast locally instead (organization-sites, beneficiaries)
    // never set meta.errorMessage, so this never double-fires for them.
    if (getRequiresConfirmationReport(error)) return;

    // Sister guard: a 409 group_in_use is a terminal refusal the delete modal explains
    // in place, naming every resource holding the group. A generic toast next to that
    // explanation would only add noise.
    if (getGroupInUseAffectedResources(error)) return;

    if (meta?.errorMessage && !isAuthFailure(error) && !isForbiddenError(error)) {
      // Prefer the structured 422 `errors` array; otherwise surface the backend
      // detail only when the mutation opted in. A single toast either way.
      const description =
        extract422ErrorsDescription(error) ??
        (meta.showApiErrorDetail ? extractApiErrorDetail(error) : null);
      toast.error(meta.errorMessage, description ? { description } : undefined);
    }
  };
};

// Cache configurations for different environments.
// refetchOnMount stays true (TanStack Query default): invalidated queries that were inactive
// at invalidation time will refetch when a consumer page mounts them. staleTime gates how
// often this actually hits the network.
const CACHE_SCENARIOS = {
  // Maximum freshness - always up-to-date data
  production: {
    staleTime: 2 * 60 * 1000, // 2 minutes tolerance to prevent excessive refetches
    gcTime: 5 * 60 * 1000, // 5 minutes cache retention
    refetchOnWindowFocus: false, // Prevent refetch when window regains focus
    refetchOnReconnect: true, // Keep refetch on reconnection for data integrity
    refetchOnMount: true,
  },
  // Balanced - good performance + freshness
  balanced: {
    staleTime: 2 * 60 * 1000, // 2 minutes tolerance
    gcTime: 5 * 60 * 1000, // 5 minutes cache retention
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: true,
  },
  // Development - longer cache for less requests
  development: {
    staleTime: 5 * 60 * 1000, // 5 minutes tolerance
    gcTime: 10 * 60 * 1000, // 10 minutes cache retention
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: true,
  },
};

const ACTIVE_SCENARIO = CACHE_SCENARIOS.balanced;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: ACTIVE_SCENARIO.staleTime,
      gcTime: ACTIVE_SCENARIO.gcTime,
      retry: (failureCount, error) => shouldRetry(failureCount, error, 2),
      refetchOnWindowFocus: ACTIVE_SCENARIO.refetchOnWindowFocus,
      refetchOnReconnect: ACTIVE_SCENARIO.refetchOnReconnect,
      refetchOnMount: ACTIVE_SCENARIO.refetchOnMount,
    },
    mutations: { retry: (failureCount, error) => shouldRetry(failureCount, error, 1) },
  },
});

setupMutationMetaHandlers(queryClient);

// Global query error handler — safety net for 401 redirect
const queryCache = queryClient.getQueryCache();

queryCache.config.onError = (error) => {
  if (isAuthFailure(error)) {
    window.location.href = "/login";
  }
};
